import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Tab, Tabs } from '@material-ui/core';
import { Formik, useFormikContext } from 'formik';
import { useIntl } from 'react-intl';
import {
  ConditionalWrapper,
  RepliconDialog
} from '~/modules/common/components';
import { useGetKeyValueSettings } from '~/modules/common/hooks';
import { PROJECT_PERMISSION } from '~/modules/common/enums';
import { useProjectResourceAllocationPermissions } from '~/modules/common/hooks/project';
import { useMeContext } from '~/modules/me';

import {
  ADD_RESOURCE_ESTIMATE_DIALOG_TABS,
  ADD_TASK_RESOURCE_ESTIMATE_DIALOG_SETTING
} from './constants';
import AddResourceEstimateDialogHeader from './AddResourceEstimateDialogHeader';
import AddResourceEstimateDialogContent2 from './AddResourceEstimateDialogContent2';
import AddResourceEstimateDialogContent3 from './AddResourceEstimateDialogContent3';
import AddResourceEstimateDialogFooter2 from './AddResourceEstimateDialogFooter2';
import useAddResourceEstimateDialogFormState from './useAddResourceEstimateDialogFormState';
import useGetTotalResourceEstimateHoursForTask from './useGetTotalResourceEstimateHoursForTask';

const useStyles = makeStyles(theme => ({
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  tab: {
    flex: 1
  }
}));

const resourceEstimateDialogTitleId = 'AddResourceEstimateDialogTitle';

const AddResourceEstimateDialog = ({
  task,
  isOpen,
  onClose,
  resourceEstimate
}) => {
  const [isDirty, setIsDirty] = useState(false);

  const classes = useStyles();
  const { formatMessage } = useIntl();

  const {
    featureFlags: { isPsaRmpMultipleRolesMilestone1Enabled }
  } = useMeContext();

  const [activeTab, setActiveTab] = useState(
    isPsaRmpMultipleRolesMilestone1Enabled && resourceEstimate?.resourceUser
      ? ADD_RESOURCE_ESTIMATE_DIALOG_TABS.RESOURCE
      : ADD_RESOURCE_ESTIMATE_DIALOG_TABS.PLACEHOLDER
  );

  const handleTabChange = useCallback((_, newTab) => setActiveTab(newTab), []);

  const checkIsDirty = useCallback(() => isDirty, [isDirty]);

  const canViewCost = Boolean(
    task.project.permittedActionUris?.includes(
      PROJECT_PERMISSION.VIEW_COST_DATA
    )
  );

  const {
    canEditResourceAllocation: isFullVersionEnabled
  } = useProjectResourceAllocationPermissions(
    task.project.resourceAllocationActions
  );

  const {
    loading,
    totalResourceEstimateHours
  } = useGetTotalResourceEstimateHoursForTask({
    taskId: task.id,
    skip: Boolean(resourceEstimate)
  });

  const {
    isLoading: keyValueSettingsLoading,
    keyValueSettings: { isCreateTaskAllocationEnabled } = {}
  } = useGetKeyValueSettings(ADD_TASK_RESOURCE_ESTIMATE_DIALOG_SETTING);

  const {
    values: { resourceEstimates },
    setFieldValue
  } = useFormikContext();

  const {
    initialValues,
    validationSchema
  } = useAddResourceEstimateDialogFormState({
    task,
    totalResourceEstimateHours,
    resourceEstimate,
    isCreateTaskAllocationEnabled,
    isFullVersionEnabled
  });

  return (
    <RepliconDialog
      maxWidth="xs"
      fullWidth
      open={isOpen}
      onClose={onClose}
      aria-labelledby={resourceEstimateDialogTitleId}
      checkIsDirty={checkIsDirty}
      enableUnsavedChangesWarning
    >
      {isPsaRmpMultipleRolesMilestone1Enabled && (
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          className={classes.tabs}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            value={ADD_RESOURCE_ESTIMATE_DIALOG_TABS.PLACEHOLDER}
            label={formatMessage({
              id: 'addResourceEstimateDialog.addPlaceholder'
            })}
            className={classes.tab}
          />
          <Tab
            value={ADD_RESOURCE_ESTIMATE_DIALOG_TABS.RESOURCE}
            label={formatMessage({
              id: 'addResourceEstimateDialog.addResource'
            })}
            className={classes.tab}
          />
        </Tabs>
      )}
      <AddResourceEstimateDialogHeader
        task={task}
        isEditMode={Boolean(resourceEstimate)}
        dialogTitleId={resourceEstimateDialogTitleId}
        activeTab={
          isPsaRmpMultipleRolesMilestone1Enabled ? activeTab : undefined
        }
      />
      {isPsaRmpMultipleRolesMilestone1Enabled ? (
        <AddResourceEstimateDialogContent3
          activeTab={activeTab}
          task={task}
          onClose={onClose}
          resourceEstimates={resourceEstimates}
          setParentFieldValue={setFieldValue}
          canViewCost={canViewCost}
          isFullVersionEnabled={isFullVersionEnabled}
          resourceEstimate={resourceEstimate}
          isCreateTaskAllocationEnabled={isCreateTaskAllocationEnabled}
          totalResourceEstimateHours={totalResourceEstimateHours}
          loading={loading || keyValueSettingsLoading}
          setIsDirty={setIsDirty}
        />
      ) : (
        <ConditionalWrapper
          condition={!(loading || keyValueSettingsLoading)}
          WrapperComponent={Formik}
          initialValues={initialValues}
          validationSchema={validationSchema}
        >
          <>
            <AddResourceEstimateDialogContent2
              isFullVersionEnabled={isFullVersionEnabled}
              loading={loading || keyValueSettingsLoading}
              task={task}
              isDirty={isDirty}
              setIsDirty={setIsDirty}
            />
            <AddResourceEstimateDialogFooter2
              projectId={task.project.id}
              resourceEstimates={resourceEstimates}
              taskId={task.id}
              onClose={onClose}
              setFieldValue={setFieldValue}
              loadingContent={loading}
              canViewCost={canViewCost}
            />
          </>
        </ConditionalWrapper>
      )}
    </RepliconDialog>
  );
};

AddResourceEstimateDialog.propTypes = {
  task: PropTypes.object,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  resourceEstimate: PropTypes.object
};

export default AddResourceEstimateDialog;
